/**
 * Sequencing archetype renderer (fix #252 / #PL-WEB-RUBRICS).
 *
 * 4 cards the kid reorders into the canonical `position` order
 * (story, comic strip, recipe, etc.). The renderer:
 *
 *   - Shuffles the cards in a bank on mount so the kid always
 *     starts un-ordered.
 *   - Uses a tap-to-select + tap-to-place fallback because WebKit
 *     drag-and-drop is unreliable on iPad Safari (per the issue
 *     body's Risks section). The kid taps a bank card to "arm"
 *     it, then taps an empty slot to place. Tapping a filled slot
 *     returns that card to the bank (self-correction path).
 *   - Records the kid's arrangement as `[cardIndex]` and calls
 *     `onTrialComplete` once a "Done" button is tapped.
 *
 * Scoring: positional permutation match against the canonical
 * positions, with optional `one_swap_tolerance` (UX leniency,
 * NOT clinical — per the issue body's Risks section).
 */
import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { SequencingRubric } from '../../../lib/practice/rubrics';
import { scoreSequencing } from '../../../lib/practice/scoring';
import type { ArchetypePlayerProps, TrialOutcome } from './shared';

interface Props extends ArchetypePlayerProps<SequencingRubric> {}

interface State {
  /** cardIndex currently in each slot, or null if empty. */
  slots: (number | null)[];
  /** Unplaced card indices, in shuffled display order. */
  bank: number[];
}

function shuffled<T>(arr: readonly T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = a[i]!;
    a[i] = a[j]!;
    a[j] = tmp;
  }
  return a;
}

function initialState(rubric: SequencingRubric): State {
  return {
    slots: Array(rubric.cards.length).fill(null),
    bank: shuffled(rubric.cards.map((_, i) => i)),
  };
}

/**
 * The canonical ordering of card indices, sorted by each
 * card's `position`. Used by the renderer for both the scoring
 * call (in `onSubmit`) and for live per-slot correctness
 * feedback (fix #421 — applying the same immediate-
 * reinforcement pattern as PictureNaming #403 to the sibling
 * archetype renderers).
 */
function expectedOrder(rubric: SequencingRubric): number[] {
  return [...rubric.cards]
    .map((c, index) => ({ index, position: c.position }))
    .sort((a, b) => a.position - b.position)
    .map((item) => item.index);
}

export default function SequencingPlayer({
  rubric,
  trialIndex,
  totalTrials,
  onTrialComplete,
}: Props): ReactElement {
  const [state, setState] = useState<State>(() => initialState(rubric));
  const [armedCard, setArmedCard] = useState<number | null>(null);
  const [startedAt, setStartedAt] = useState<number>(() => Date.now());
  // Self-correction channel: tap/drag a placed card back to the bank
  // and re-place. Per `rubrics.ts` this gesture is the correction
  // signal for sequencing (no time window — the bank↔slot round-trip
  // IS the correction).
  const hasReturnedToBankRef = useRef(false);

  useEffect(() => {
    setStartedAt(Date.now());
    setState(initialState(rubric));
    setArmedCard(null);
    hasReturnedToBankRef.current = false;
  }, [trialIndex, rubric]);

  function placeCard(slot: number, card: number): void {
    setState((prev) => {
      if (!prev.bank.includes(card)) return prev;
      if (prev.slots[slot] !== null) return prev;
      const nextBank = prev.bank.filter((c) => c !== card);
      const nextSlots = prev.slots.slice();
      nextSlots[slot] = card;
      return { slots: nextSlots, bank: nextBank };
    });
    setArmedCard(null);
  }

  function returnToBank(slot: number): void {
    // Only count as a self-correction if the slot actually held a card
    // at the time of the gesture. The state updater no-ops on an empty
    // slot, so we must guard the analytics flag the same way to avoid
    // over-reporting `self_corrected=true` for no-op gestures (#358).
    if (state.slots[slot] === null) return;
    setState((prev) => {
      const card = prev.slots[slot]!;
      if (card === null) return prev;
      const nextSlots = prev.slots.slice();
      nextSlots[slot] = null;
      const nextBank = prev.bank.includes(card)
        ? prev.bank
        : [...prev.bank, card];
      return { slots: nextSlots, bank: nextBank };
    });
    setArmedCard(null);
    hasReturnedToBankRef.current = true;
  }

  function onArmCard(card: number): void {
    if (!state.bank.includes(card)) return;
    setArmedCard((prev) => (prev === card ? null : card));
  }

  function onSlotClick(slot: number): void {
    const cardInSlot = state.slots[slot];
    if (armedCard !== null && cardInSlot === null) {
      placeCard(slot, armedCard);
    } else if (cardInSlot !== null) {
      returnToBank(slot);
    }
  }

  function onSubmit(): void {
    const response: number[] = state.slots.map((s) => s ?? -1);
    if (response.some((v) => v < 0)) return;
    const score = scoreSequencing(response, rubric);
    const expected = expectedOrder(rubric);
    const outcome: TrialOutcome = {
      isCorrect: score.is_correct,
      latencyMs: Date.now() - startedAt,
      selfCorrected: hasReturnedToBankRef.current,
      usedCue: false,
      response,
      expected,
      promptKind: 'picture',
    };
    onTrialComplete(outcome);
  }

  const filledCount = state.slots.filter((s): s is number => s !== null).length;
  const allPlaced = filledCount === rubric.cards.length;

  // Compute the outcome-as-of-the-current-placement so every
  // filled slot can show the kid a correct/incorrect cue the
  // instant they place a card (fix #421 — applying the same
  // immediate-reinforcement pattern as PictureNaming #403 to
  // the sibling archetype renderers). Per-slot scoring: card at
  // `slots[i]` is correct iff it matches the canonical
  // `expectedOrder[i]`. The visual cue persists from the place
  // gesture until the kid advances; tapping a filled slot to
  // return-to-bank and re-placing re-evaluates the cue. We
  // deliberately surface right/wrong per slot rather than
  // hiding the outcome — sequencing is targeted at kids old
  // enough to process immediate correctness feedback, which
  // is the entire pedagogical point of an ordering trial.
  const expected = expectedOrder(rubric);
  const slotCorrectCount = state.slots.reduce<number>(
    (acc, card, i) => (card !== null && card === expected[i] ? acc + 1 : acc),
    0,
  );
  const allSlotsCorrect =
    allPlaced && slotCorrectCount === rubric.cards.length;

  return (
    <main className="screen handoff">
      <div className="auth-hero">
        <header className="screen-header">
          <h1>Trial {trialIndex + 1} / {totalTrials}</h1>
          <p className="tagline">
            Put the cards in the right order.
          </p>
        </header>
      </div>
      <div
        className="sequencing-slots"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '8px',
          maxWidth: '320px',
          margin: '0 auto',
        }}
        role="list"
        aria-label="Card slots"
      >
        {state.slots.map((slotCardIdx, slotIdx) => {
          const card =
            slotCardIdx === null ? null : rubric.cards[slotCardIdx];
          const expectedCardIdx = expected[slotIdx];
          const slotCorrect =
            slotCardIdx !== null && slotCardIdx === expectedCardIdx;
          const outcomeClass =
            slotCardIdx !== null && slotCorrect
              ? ' selected correct'
              : slotCardIdx !== null && !slotCorrect
                ? ' selected incorrect'
                : allPlaced && slotCardIdx === null
                  ? ' muted'
                  : '';
          const expectedPosition =
            expectedCardIdx !== undefined
              ? rubric.cards[expectedCardIdx]?.position
              : undefined;
          return (
            <button
              type="button"
              key={`slot-${slotIdx}`}
              className={`snap-button${outcomeClass}`}
              onClick={(): void => onSlotClick(slotIdx)}
              aria-label={
                card && slotCorrect
                  ? `Slot ${slotIdx + 1}: card ${slotCardIdx}, correct, tap to return to bank`
                  : card && !slotCorrect
                    ? `Slot ${slotIdx + 1}: card ${slotCardIdx}, wrong; expected card at position ${(expectedPosition ?? -1) + 1}, tap to return to bank`
                    : card
                      ? `Slot ${slotIdx + 1}: card ${slotCardIdx}, tap to return to bank`
                      : armedCard !== null
                        ? `Empty slot ${slotIdx + 1}, tap to place armed card`
                        : `Empty slot ${slotIdx + 1}`
              }
              style={{ minHeight: '96px' }}
            >
              {card ? (
                card.image_url ? (
                  <img
                    src={card.image_url}
                    alt=""
                    style={{ maxWidth: '100%', maxHeight: '80px' }}
                  />
                ) : (
                  <span>Card {card.position + 1}</span>
                )
              ) : (
                <span aria-hidden="true">…</span>
              )}
            </button>
          );
        })}
      </div>
      <p className="tagline">
        Card bank — tap one, then tap an empty slot:
      </p>
      <div
        className="sequencing-bank"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          justifyContent: 'center',
          maxWidth: '320px',
          margin: '0 auto',
        }}
        role="list"
        aria-label="Card bank"
      >
        {state.bank.map((cardIdx) => {
          const card = rubric.cards[cardIdx]!;
          const armed = armedCard === cardIdx;
          return (
            <button
              type="button"
              key={`bank-${cardIdx}`}
              className={`snap-button${armed ? ' selected' : ''}`}
              onClick={(): void => onArmCard(cardIdx)}
              aria-label={
                armed
                  ? `Card ${cardIdx} armed, tap an empty slot to place`
                  : `Card ${cardIdx}, tap to arm for placement`
              }
              style={{ minHeight: '80px' }}
            >
              {card.image_url ? (
                <img
                  src={card.image_url}
                  alt=""
                  style={{ maxWidth: '100%', maxHeight: '64px' }}
                />
              ) : (
                <span>Card {card.position + 1}</span>
              )}
            </button>
          );
        })}
      </div>
      {armedCard !== null ? (
        <p className="tagline" role="status">
          Tap an empty slot to place this card, or tap it again to disarm.
        </p>
      ) : null}
      <button
        type="button"
        className="snap-button"
        onClick={onSubmit}
        disabled={!allPlaced}
        aria-label={
          allSlotsCorrect
            ? `Done — all ${slotCorrectCount} cards correct`
            : `Done — ${slotCorrectCount} of ${filledCount || rubric.cards.length} cards correct`
        }
      >
        Done
      </button>
    </main>
  );
}
